import { createFileRoute } from "../lib/router-compat";
import { useState, useEffect, Suspense, useRef } from "react";
import { Link, useNavigate } from "../lib/router-compat";
import { RotateCcw, Play, Pause, ChevronLeft, Sun, Moon } from "lucide-react";
import type { SinglePanSceneHandle, LightingOverrides } from "../components/visualizer/SinglePanScene";
import SinglePanScene from "../components/visualizer/SinglePanScene";
import { useLightingPreset } from "../hooks/useLightingPreset";

const TOP_FINISHES = [
  { id: "hi-gloss-black", label: "Hi-Gloss Black", color: "#0a0a0a" },
  { id: "matte-black", label: "Matte Black", color: "#1a1a1a" },
  { id: "chrome", label: "Chrome", color: "#c8c8c8" },
  { id: "powder-white", label: "Painted White", color: "#e8e4dc" },
];

const BARREL_FINISHES = [
  { id: "hi-gloss-black", label: "Hi-Gloss Black", color: "#0a0a0a" },
  { id: "matte-black", label: "Matte Black", color: "#1a1a1a" },
  { id: "powder-blue", label: "Bengal Blue", color: "#2a4a7a" },
  { id: "powder-copper", label: "Candy Copper", color: "#b87333" },
  { id: "powder-red", label: "Red", color: "#8a2a2a" },
  { id: "powder-green", label: "Green", color: "#2a5a3a" },
];

export const Route = createFileRoute("/visualizer/bass")({
  head: () => ({
    meta: [
      { title: "Bass Pan — Steelpan Visualizer" },
      { name: "description", content: "Bass steelpan product showcase with interactive finishes." },
      { property: "og:title", content: "Bass Pan — Steelpan Visualizer" },
      { property: "og:description", content: "Bass steelpan product showcase with interactive finishes." },
    ],
  }),
  component: BassPage,
});

function ControlButton({ onClick, children, label, active }: { onClick: () => void; children: React.ReactNode; label: string; active?: boolean }) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-md ${
        active
          ? "bg-white/20 text-white border border-white/30"
          : "bg-white/5 text-white/50 hover:text-white hover:bg-white/10 border border-white/10"
      }`}
    >
      {children}
    </button>
  );
}

function FinishRow({ label, options, selected, onSelect }: {
  label: string;
  options: { id: string; label: string; color: string }[];
  selected: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-white/40 text-[10px] tracking-[0.2em] uppercase w-14 text-right shrink-0">{label}</span>
      <div className="flex items-center gap-1 p-1 rounded-full bg-white/5 backdrop-blur-md border border-white/10">
        {options.map((f) => (
          <button
            key={f.id}
            onClick={() => onSelect(f.id)}
            title={f.label}
            className={`relative w-7 h-7 rounded-full transition-all duration-200 ${
              selected === f.id ? "scale-110 ring-2 ring-white/40 ring-offset-1 ring-offset-black" : "hover:scale-105"
            }`}
          >
            <div
              className="w-full h-full rounded-full border border-white/20"
              style={{ background: f.color }}
            />
          </button>
        ))}
      </div>
      <span className="text-white text-[10px] tracking-[0.15em] uppercase w-24">
        {options.find((f) => f.id === selected)?.label}
      </span>
    </div>
  );
}

function BassPage() {
  const [mounted, setMounted] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [autoRotate, setAutoRotate] = useState(true);
  const [topFinish, setTopFinish] = useState("hi-gloss-black");
  const [barrelFinish, setBarrelFinish] = useState("hi-gloss-black");
  const { envPreset, togglePreset } = useLightingPreset();
  const defaultLighting: LightingOverrides = { ambient: 0.8, spotFront: 3.0, spotBack: 2.5, pointFill: 1.0, envIntensity: 8.0 };
  const [lighting, setLighting] = useState<LightingOverrides>(defaultLighting);
  const sceneRef = useRef<SinglePanSceneHandle>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
    const timer = setTimeout(() => setShowHint(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  if (!mounted) return <div className="flex min-h-screen items-center justify-center" style={{ background: "#000" }}><p className="text-white/40 text-sm tracking-widest uppercase animate-pulse">Loading…</p></div>;

  return (
    <div className="relative w-full h-screen overflow-hidden" style={{ background: "#000" }}>
      <Suspense fallback={<div className="flex min-h-screen items-center justify-center" style={{ background: "#000" }}><p className="text-white/40 text-sm tracking-widest uppercase animate-pulse">Loading…</p></div>}>
        <SinglePanScene
          ref={sceneRef}
          modelUrl="models/bass.glb"
          rotation={[0, -Math.PI / 2, 0]}
          cameraPosition={[0, 2.0, 1.8]}
          darkMode
          autoRotate={autoRotate}
          dualFinish={{ top: topFinish, barrel: barrelFinish }}
          envPreset={envPreset}
          lighting={lighting}
        />
      </Suspense>

      {/* Top nav */}
      <div className="absolute top-20 left-0 right-0 flex items-center justify-between px-8 py-2 z-10">
        <Link to="/visualizer" className="flex items-center gap-2 text-white/50 hover:text-white text-xs tracking-[0.3em] uppercase transition-colors">
          <ChevronLeft size={14} />
          Back
        </Link>
        <div className="flex items-center gap-1 p-1 rounded-full bg-white/5 backdrop-blur-md border border-white/10">
          <button onClick={() => navigate({ to: "/visualizer/tenor" })} className="px-3 py-1.5 rounded-full text-[10px] tracking-[0.15em] uppercase text-white/40 hover:text-white/70 hover:bg-white/5 border border-transparent">Tenor</button>
          <button onClick={() => navigate({ to: "/visualizer/cello" })} className="px-3 py-1.5 rounded-full text-[10px] tracking-[0.15em] uppercase text-white/40 hover:text-white/70 hover:bg-white/5 border border-transparent">Cello</button>
          <button onClick={() => navigate({ to: "/visualizer/bass" })} className="px-3 py-1.5 rounded-full text-[10px] tracking-[0.15em] uppercase bg-white/15 text-white border border-white/20">Bass</button>
        </div>
        <div className="w-16" />
      </div>

      {/* Right side controls */}
      <div className="absolute right-6 top-1/2 -translate-y-1/2 flex flex-col gap-3 z-10">
        <ControlButton onClick={togglePreset} label="Toggle lighting" active={envPreset === "night"}>
          {envPreset === "studio" ? <Sun size={16} /> : <Moon size={16} />}
        </ControlButton>
        <ControlButton onClick={() => setAutoRotate(!autoRotate)} label={autoRotate ? "Stop rotation" : "Auto rotate"} active={autoRotate}>
          {autoRotate ? <Pause size={16} /> : <Play size={16} />}
        </ControlButton>
        <ControlButton onClick={() => sceneRef.current?.resetView()} label="Reset view">
          <RotateCcw size={16} />
        </ControlButton>
      </div>

      {/* Dual finish picker — side by side */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-6">
        <FinishRow label="Top" options={TOP_FINISHES} selected={topFinish} onSelect={setTopFinish} />
        <div className="w-px h-8 bg-white/10" />
        <FinishRow label="Barrel" options={BARREL_FINISHES} selected={barrelFinish} onSelect={setBarrelFinish} />
      </div>

      {/* Interaction hint */}
      <div
        className="absolute bottom-32 left-1/2 -translate-x-1/2 z-10 transition-opacity duration-1000 pointer-events-none"
        style={{ opacity: showHint ? 1 : 0 }}
      >
        <p className="text-white/20 text-[10px] tracking-[0.3em] uppercase">
          Drag to rotate · Scroll to zoom
        </p>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/60 to-transparent pointer-events-none z-0" />
    </div>
  );
}
